import { defineComponent } from 'vue';
import { mapState, mapGetters } from 'vuex';
import SearchService from '@/services/SearchService.ts';
import CustomerForm from '@/components/customer/Form.vue';
import CustomerAvatar from '@/components/customer/Avatar.vue';
import PrintRegistrationCard from '@/components/customer/PrintRegistrationCard.vue';
export default defineComponent({
    name: 'CustomerSelector',
    components: {
        CustomerForm,
        CustomerAvatar,
        PrintRegistrationCard
    },
    props: {
        value: Object,
        outlined: Boolean,
        isMobile: Boolean,
        disabled: Boolean,
        hideCustomerFormButton: Boolean,
        label: String,
        mandatory: {
            type: Boolean,
            default: false
        },
        /** Om true, aktiveras SCR-scanner-lyssnare automatiskt */
        scrListening: {
            type: Boolean,
            default: false
        },
        /** Om true, dölj CKE-badgen och SCR-funktionalitet */
        hideCke: {
            type: Boolean,
            default: false
        }
    },
    emits: {
        input: (value) => true,
        'update:model-value': (value) => true
    },
    data: () => ({
        customerTypes: [
            { text: 'Privatperson', isOrg: false },
            { text: 'Företagskund', isOrg: true }
        ],
        editedCustomer: null,
        searchValue: null,
        searchResults: [],
        isLoading: false,
        showCustomerForm: false,
        // SCR/CKE
        isScrListenerActive: false,
        scrSearchDebounceTimer: 0,
        scrSettingCustomer: false,
        scrSearchPending: false
    }),
    computed: {
        ...mapState({
            // @ts-expect-error: vuex is not typed
            defaultPrinter: state => state.workstation.default_printer
        }),
        ...mapGetters({
            customerTemplate: 'customer/customerTemplate',
            scrIsEnabled: 'scr/isEnabled'
        }),
        scrSearchResult() {
            return this.$store.state.scr.searchResult;
        },
        isScrSearching() {
            return this.$store.state.scr.isSearching;
        },
        activeCkeCardNumber() {
            // Prioritera skannat kort, fallback till kundens befintliga kort
            return this.$store.state.scr.pendingCardNumber || this.customer?.cke_card_number || null;
        },
        activeCkeCardFormatted() {
            const scr = this.scrSearchResult?.scr;
            if (scr?.card_number === this.activeCkeCardNumber && scr?.card_number_formatted) {
                return this.formatCardNumber(scr.card_number_formatted);
            }
            const num = this.activeCkeCardNumber;
            if (!num) {
                return '';
            }
            return this.formatCardNumber(num);
        },
        activeCkeBadgeColor() {
            const scr = this.scrSearchResult?.scr;
            if (scr?.card_number !== this.activeCkeCardNumber) {
                return 'primary';
            }
            // ACTIVE men is_valid=false → utgånget kort, visa som warning
            if (scr.card_status === 'ACTIVE' && scr.is_valid === false) {
                return 'warning';
            }
            if (scr.card_status === 'ACTIVE') {
                return 'success';
            }
            if (scr.card_status === 'EXPIRED') {
                return 'warning';
            }
            if (scr.card_status === 'BLOCKED') {
                return 'error';
            }
            if (scr.card_status === 'INACTIVE' || scr.card_status === 'ANONYMOUS') {
                return 'grey';
            }
            if (scr.card_status === 'AWAITS_ACTIVATING' || scr.card_status === 'AWAITING_ACTIVATING') {
                return 'info';
            }
            return 'primary';
        },
        customer: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
                this.$emit('update:model-value', val);
            }
        },
        isExistingCustomer: function () {
            return !!this.customer?.id;
        },
        customerFormHeader: function () {
            return 'Ny kund';
        },
        buttonText: function () {
            if (this.isExistingCustomer) {
                return 'Redigera Kund';
            }
            else {
                return 'Ny Kund';
            }
        },
        selectorItems: function () {
            if (!this.searchValue) {
                if (!this.customer) {
                    return [];
                }
                return [this.customer];
            }
            if (this.customer && !this.searchResults.find(c => c.id === this.customer?.id)) {
                return [this.customer, ...this.searchResults];
            }
            return this.searchResults;
        },
        showPrintRegistrationCardButton: function () {
            return this.editedCustomer && !this.editedCustomer.is_org && this.defaultPrinter;
        }
    },
    methods: {
        openCustomerForm: function () {
            if (this.customer) {
                this.editedCustomer = Object.assign({}, this.customer);
            }
            else {
                this.editedCustomer = Object.assign({}, this.customerTemplate);
            }
            this.showCustomerForm = true;
        },
        closeCustomerForm: function () {
            this.showCustomerForm = false;
        },
        customerSaved: function (savedCustomer) {
            const searchResultsIndex = this.searchResults.findIndex(c => c.id === savedCustomer.id);
            if (searchResultsIndex !== -1) {
                // Byter ut den gamla kunden i sökresultaten mot den uppdaterade
                this.searchResults.splice(searchResultsIndex, 1, savedCustomer);
            }
            this.customer = savedCustomer;
            this.closeCustomerForm();
        },
        //
        // SCR/CKE scanner-integration
        //
        startScrListener: function () {
            if (!this.scrIsEnabled || this.isScrListenerActive) {
                return;
            }
            window.scannerVM?.requestInput('scr/handleScannerInput', null, true);
            this.isScrListenerActive = true;
        },
        stopScrListener: function () {
            if (!this.isScrListenerActive) {
                return;
            }
            window.scannerVM?.abortInputRequest('scr/handleScannerInput');
            this.isScrListenerActive = false;
        },
        clearCkeCard: function () {
            this.$store.commit('scr/SET_PENDING_CARD_NUMBER', null);
            this.$store.commit('scr/SET_SEARCH_RESULT', null);
        },
        /**
         * Formatera kortnummer — extrahera siffror ur v[...]-format (virtuella kort)
         */
        formatCardNumber: function (num) {
            const vMatch = num.match(/v\[([^\]]+)\]/);
            if (vMatch) {
                return vMatch[1].replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
            }
            return num.replace(/\s+/g, '').replace(/(.{4})/g, '$1 ').trim();
        },
        /**
         * Handle SCR result from scanner input — auto-selects customer or opens form.
         * Only called for scanner-triggered searches, NOT typed searches.
         */
        handleScrResult: function (result) {
            if (!result) {
                return;
            }
            const source = result.source;
            const customer = result.customer;
            const scr = result.scr;
            if (source === 'none') {
                return;
            }
            // Om en kund redan är vald, ta bara kortnumret
            if (this.customer?.id && scr?.card_number) {
                this.$store.commit('scr/SET_PENDING_CARD_NUMBER', scr.card_number);
                return;
            }
            // Rensa tidigare CKE-data innan ny skanning bearbetas
            this.$store.commit('scr/SET_PENDING_CARD_NUMBER', scr?.card_number || null);
            // Prevent the searchValue watcher from clearing the customer we're about to set
            this.scrSettingCustomer = true;
            if (source === 'local' || source === 'scr_linked') {
                // Lokal kund hittad — välj direkt
                if (customer && customer.id) {
                    window.enrich.enrichCustomer(customer);
                    if (scr?.card_number) {
                        customer.cke_card_number = scr.card_number;
                    }
                    this.customer = customer;
                    this.searchValue = null;
                }
            }
            else if (source === 'local_multiple') {
                // Flera lokala kunder — visa i sökresultaten
                if (result.customers) {
                    window.enrich.enrichCustomer(result.customers);
                    this.searchResults = result.customers;
                }
            }
            else if (source === 'scr' || source === 'decoded_data') {
                // SCR-data eller avkodad scannerdata utan lokal kund — prefill kundformulär
                if (customer && customer.id === 0) {
                    const prefill = Object.assign({}, this.customerTemplate, customer);
                    if (scr?.card_number) {
                        prefill.cke_card_number = scr.card_number;
                    }
                    const countryId = customer.country_id || customer.dialling_code_country_id;
                    if (countryId) {
                        const allCountries = this.$store.state.staticData?.countries || [];
                        const matchedCountry = allCountries.find((c) => c.id === countryId);
                        if (matchedCountry) {
                            prefill.country = matchedCountry;
                            prefill.country_id = matchedCountry.id;
                            if (!prefill.dialling_code_country_id) {
                                prefill.dialling_code_country_id = matchedCountry.id;
                            }
                        }
                    }
                    this.editedCustomer = prefill;
                    this.showCustomerForm = true;
                    this.searchValue = null;
                }
            }
            // source === 'none' → ingen träff, gör inget
            this.$nextTick(() => { this.scrSettingCustomer = false; });
        }
    },
    watch: {
        searchValue: function () {
            clearTimeout(this.scrSearchDebounceTimer);
            if (window.scannerVM?.restoringInput) {
                this.scrSearchPending = false;
                this.isLoading = false;
                return;
            }
            const v = this.searchValue;
            if (v === '' || v == null) {
                this.searchResults = [];
                // Only clear customer if this wasn't triggered by SCR auto-selecting a customer
                if (!this.scrSettingCustomer) {
                    this.customer = null;
                }
                this.isLoading = false;
                return false;
            }
            // Trigger SCR-sökning parallellt (kortnummer, personnummer, telefon)
            if (this.scrIsEnabled && !this.hideCke && typeof this.searchValue === 'string') {
                const sv = this.searchValue;
                const digitsOnly = sv.replace(/\D/g, '');
                let scrQuery = null;
                // Virtuellt kort-format: v[210799 034 1275]
                const vMatch = sv.match(/v\[([^\]]+)\]/);
                if (vMatch) {
                    scrQuery = vMatch[1].replace(/\s+/g, '');
                    // Telefon: internationellt format (+46... eller 0046...) — FÖRE kortnummer pga 0046 = 13 siffror
                }
                else if (/^(\+|00)\d{8,}$/.test(sv.trim())) {
                    scrQuery = sv.trim();
                    // Kortnummer (13/16) eller Personnummer (10/12) — rena siffror
                }
                else if (sv === digitsOnly && [10, 12, 13, 16].includes(digitsOnly.length)) {
                    scrQuery = digitsOnly;
                    // Telefon: svenskt mobilnummer med separatorer (073 904 9368)
                }
                else if (/^0[1-9]/.test(sv.trim()) && digitsOnly.length === 10) {
                    scrQuery = digitsOnly;
                    // Telefon: börjar med 46 utan + (46739049368)
                }
                else if (/^46\d{7,12}$/.test(digitsOnly)) {
                    scrQuery = digitsOnly;
                    // Telefon: 7-9 siffror utan prefix (739049368)
                }
                else if (sv === digitsOnly && digitsOnly.length >= 7 && digitsOnly.length <= 9) {
                    scrQuery = digitsOnly;
                    // E-post
                }
                else if (sv.trim().includes('@') && sv.trim().length >= 5) {
                    scrQuery = sv.trim();
                }
                if (scrQuery) {
                    const q = scrQuery;
                    this.scrSearchPending = true;
                    this.scrSearchDebounceTimer = window.setTimeout(() => {
                        this.$store.dispatch('scr/search', q);
                    }, 400);
                }
            }
            // Normal kundsökning körs alltid parallellt
            this.isLoading = true;
            SearchService.searchCustomer(v)
                .then(({ data }) => {
                if (v === this.searchValue) {
                    window.enrich.enrichCustomer(data.data.customers);
                    this.searchResults = data.data.customers;
                }
            })
                .finally(() => {
                if (v === this.searchValue) {
                    this.isLoading = false;
                }
            });
        },
        // Starta/stoppa SCR-scanner-lyssnare baserat på scrListening prop
        scrListening: {
            immediate: true,
            handler(val) {
                if (val) {
                    this.startScrListener();
                }
                else {
                    this.stopScrListener();
                }
            }
        },
        // Rensa CKE-badge när kund avmarkeras
        customer: function (val) {
            if (!val && !this.scrSettingCustomer) {
                this.clearCkeCard();
            }
        },
        // Reagera på SCR-sökresultat
        scrSearchResult: function (result) {
            if (!result) {
                return;
            }
            if (this.isScrListenerActive) {
                // Scanner input → full auto-select/prefill flow
                this.handleScrResult(result);
            }
            else if (this.scrSearchPending) {
                // Typed search → merge SCR results into dropdown, don't auto-select
                this.scrSearchPending = false;
                const source = result.source;
                if ((source === 'local' || source === 'scr_linked') && result.customer?.id) {
                    const cust = result.customer;
                    window.enrich.enrichCustomer(cust);
                    if (result.scr?.card_number) {
                        cust.cke_card_number = result.scr.card_number;
                    }
                    if (!this.searchResults.find((c) => c.id === cust.id)) {
                        this.searchResults = [cust, ...this.searchResults];
                    }
                }
                else if (source === 'local_multiple' && result.customers?.length) {
                    window.enrich.enrichCustomer(result.customers);
                    const existingIds = new Set(this.searchResults.map((c) => c.id));
                    const newCustomers = result.customers.filter((c) => !existingIds.has(c.id));
                    if (newCustomers.length) {
                        this.searchResults = [...newCustomers, ...this.searchResults];
                    }
                }
                // source='none' or 'scr' with no local match → badge updates from store, no dropdown change
            }
        }
    },
    beforeDestroy() {
        this.stopScrListener();
        // Rensa SCR-sökdata
        if (this.scrIsEnabled) {
            this.$store.dispatch('scr/clearSearch');
        }
    }
});
